/**
 * Gjenbrukbar funksjon for å generere PDF for sanger
 * Brukes både i TranspositionsTab og i produksjonsplan-visning
 */

import { lagOppsett } from './sang/oppsett';
import { aapnePdf, tegnPdf } from './sang/pdf';
import { tolkSang } from './sang/tolkSang';
import { tolkGrunntone } from './sang/toneart';
import { kunTekst, lagHode, visSang } from './sang/visSang';

/**
 * Interface for PDF-generering fra sang-data
 */
export interface GenerateSongPDFOptions {
  /** Sangtittel */
  title: string;
  /** Artist */
  artist?: string;
  /** Tekst og akkorder (raw string) */
  tekstOgAkkorder: string;
  /** Antall semitoner å transponere (0 = original) */
  semitones: number;
  /** Original grunntone (f.eks. 'C'). Uten den viser hodet ingen toneart */
  originalGrunntone?: string;
  /** Original toneart type (f.eks. 'dur' eller 'moll') */
  originalToneartType?: string;
  /** Fontstørrelse for tekst (default: 12) */
  fontSize?: number;
  /** Fontstørrelse for akkorder (default: 14) */
  chordFontSize?: number;
  /** Farge for akkorder (hex, default: '#2563eb') */
  chordColor?: string;
  /** Antall kolonner (default: 1) */
  columns?: 1 | 2;
  /** Kun tekst (uten akkorder) - default: false */
  textOnly?: boolean;
  /** `bokstaver` skriver tall-akkorder som bokstaver i tonearten (default: 'original') */
  visning?: 'original' | 'bokstaver';
}

/**
 * Generer PDF for en sang med gitt toneart
 *
 * @param options - Konfigurasjon for PDF-generering
 */
export function generateSongPDF(options: GenerateSongPDFOptions): void {
  const {
    title,
    artist,
    tekstOgAkkorder,
    semitones = 0,
    originalGrunntone,
    originalToneartType,
    fontSize = 12,
    chordFontSize = 14,
    chordColor = '#2563eb',
    columns = 1,
    textOnly = false,
    visning = 'original',
  } = options;

  if (!tekstOgAkkorder) {
    throw new Error('Ingen tekst å laste ned');
  }

  const dokument = tolkSang(tekstOgAkkorder);
  const valg = { toneart: tolkGrunntone(originalGrunntone, originalToneartType), semitoner: semitones, visning };
  const linjer = textOnly ? kunTekst(dokument) : visSang(dokument, valg);
  const oppsett = lagOppsett(linjer, { fontSize, chordFontSize, columns });
  aapnePdf(tegnPdf(oppsett, lagHode(dokument, { tittel: title, artist }, valg), chordColor));
}
